import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { MessageKey } from '../../i18n';

/** Not yet in lib.dom.d.ts. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform = 'ios' | 'macSafari' | 'android' | 'desktop' | 'unsupported';

/** Copy key for the per-platform manual instructions, shown whenever the
 *  browser hasn't offered a native `beforeinstallprompt` (iOS/Safari never
 *  do; Chromium-based browsers sometimes haven't yet, or the user is on a
 *  browser without install support at all). */
export const INSTALL_HINT_KEY: Readonly<Record<InstallPlatform, MessageKey>> = {
  ios: 'home.install.ios',
  macSafari: 'home.install.macSafari',
  android: 'home.install.android',
  desktop: 'home.install.desktop',
  unsupported: 'home.install.unsupported',
};

/** Best-effort guess from the UA string — there's no reliable feature-test
 *  for "can this browser install a PWA, and how". iPadOS 13+ reports as a
 *  Mac (`navigator.platform`), distinguished from real macOS Safari only by
 *  its touch support. */
function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent;
  // UA substrings first: check the touch+MacIntel iPad fallback only once
  // neither has already given a definite answer, so a device that spoofs
  // `navigator.platform` as 'MacIntel' while its UA plainly says Android
  // (seen in at least one browser-automation environment) isn't misread
  // as an iPad.
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  const isChromiumBased = /Chrome|Chromium|Edg\/|OPR\//.test(ua);
  if (/Macintosh/.test(ua) && !isChromiumBased) return 'macSafari';
  if (isChromiumBased) return 'desktop';
  return 'unsupported';
}

function standaloneQuery(): MediaQueryList {
  return window.matchMedia('(display-mode: standalone)');
}

function subscribeStandalone(onChange: () => void): () => void {
  const mq = standaloneQuery();
  mq.addEventListener('change', onChange);
  window.addEventListener('appinstalled', onChange);
  return () => {
    mq.removeEventListener('change', onChange);
    window.removeEventListener('appinstalled', onChange);
  };
}

function getStandaloneSnapshot(): boolean {
  // `navigator.standalone` is Safari's pre-standard equivalent — iOS/iPadOS
  // never matches the `display-mode` media query.
  return standaloneQuery().matches || (window.navigator as { standalone?: boolean }).standalone === true;
}

/**
 * Bridges the browser's install lifecycle into the UI for `HomeView.tsx`'s
 * install card. `platform` is a best-effort UA guess used only to pick which
 * manual instructions to show; `canPrompt` reflects the actual, ground-truth
 * capability (the browser firing `beforeinstallprompt`) and gates whether a
 * real "Install" button appears at all — so a wrong platform guess degrades
 * to slightly-off manual instructions rather than a broken button.
 */
export function useInstallPrompt(): {
  platform: InstallPlatform;
  installed: boolean;
  canPrompt: boolean;
  promptInstall: () => Promise<void>;
} {
  const installed = useSyncExternalStore(subscribeStandalone, getStandaloneSnapshot);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    function onBeforeInstallPrompt(e: Event): void {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    }
    function onInstalled(): void {
      setDeferred(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }, [deferred]);

  return { platform: detectPlatform(), installed, canPrompt: deferred !== null, promptInstall };
}
